import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { PoModalAction, PoModalComponent } from '@po-ui/ng-components';

import { Review, SmartReviewJobFull } from '../../interfaces/review.interface';
import { TERMINAL_STATUSES } from '../../review-constants';

type WizardStep = 'job-select' | 'mode-select';

export interface JobChoice {
  label: string;
  description: string;
  jobKey: 'review' | 'assistant';
  job: SmartReviewJobFull;
  /**
   * 'generate' é a análise que nunca foi pedida, e não um reprocessamento.
   *
   * Não há job a repetir: o payload do review não pedia o assistente, então reprocessá-lo
   * refaz o mesmo trabalho e volta ao mesmo lugar. O modo all/missing também não se aplica
   * — não há resultado anterior a descartar ou preservar.
   */
  kind?: 'retry' | 'generate';
}

export interface RetryRequest {
  jobId: string;
  rerunMode: 'all' | 'missing';
  jobKey: 'review' | 'assistant';
  resourceId: string;
  resourceType: string;
  kind?: 'retry' | 'generate';
}

@Component({
  selector: 'app-retry-job-modal',
  templateUrl: './retry-job-modal.component.html',
  styleUrls: ['./retry-job-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RetryJobModalComponent {
  @ViewChild('retryModal', { static: true }) modal!: PoModalComponent;
  @Output() retryConfirmed = new EventEmitter<RetryRequest>();

  constructor(private cdr: ChangeDetectorRef) {}

  currentStep: WizardStep = 'job-select';
  choices: JobChoice[] = [];
  selectedChoice: JobChoice | null = null;
  selectedMode: 'all' | 'missing' | null = null;
  private review: Review | null = null;

  confirmAction: PoModalAction = { label: 'Confirmar', action: () => this.confirm(), disabled: true };
  cancelAction: PoModalAction = { label: 'Cancelar', action: () => this.close() };

  open(review: Review): void {
    this.review = review;
    this.choices = this.buildJobChoices(review);
    this.selectedChoice = null;
    this.selectedMode = null;
    this.currentStep = 'job-select';
    this.updateActions();

    if (!this.choices.length) {
      this.cdr.markForCheck();
      return;
    }

    // Se só existe um job retryable, pula direto para seleção de modo — exceto a geração,
    // que não tem modo a escolher.
    if (this.choices.length === 1) {
      this.selectJob(this.choices[0]);
    }

    this.modal.open();
    this.cdr.markForCheck();
  }

  close(): void {
    this.modal.close();
  }

  selectJob(choice: JobChoice): void {
    this.selectedChoice = choice;
    // Gerar não tem modo: não há resultado anterior a descartar nem a preservar. Confirmar
    // direto evita pedir uma escolha que não muda nada.
    this.selectedMode = choice.kind === 'generate' ? 'all' : null;
    this.currentStep = choice.kind === 'generate' ? 'job-select' : 'mode-select';
    this.updateActions();
    this.cdr.markForCheck();
  }

  selectMode(mode: 'all' | 'missing'): void {
    this.selectedMode = mode;
    this.updateActions();
    this.cdr.markForCheck();
  }

  goBack(): void {
    if (this.currentStep === 'mode-select' && this.choices.length > 1) {
      this.currentStep = 'job-select';
      this.selectedChoice = null;
      this.selectedMode = null;
      this.updateActions();
    }
  }

  private confirm(): void {
    if (!this.selectedChoice || !this.selectedMode || !this.review) {
      return;
    }
    const jobId = this.selectedChoice.job.id;
    if (!jobId) {
      return;
    }
    this.retryConfirmed.emit({
      jobId,
      rerunMode: this.selectedMode,
      jobKey: this.selectedChoice.jobKey,
      resourceId: this.review.resourceId,
      resourceType: this.review.resourceType,
      kind: this.selectedChoice.kind ?? 'retry'
    });
    this.close();
  }

  private updateActions(): void {
    const canConfirm = !!this.selectedChoice && !!this.selectedMode;
    this.confirmAction = { ...this.confirmAction, disabled: !canConfirm };

    if (this.currentStep === 'mode-select' && this.choices.length > 1) {
      this.cancelAction = { label: 'Voltar', action: () => this.goBack() };
    } else {
      this.cancelAction = { label: 'Cancelar', action: () => this.close() };
    }
  }

  private buildJobChoices(review: Review): JobChoice[] {
    const choices: JobChoice[] = [];
    const reviewJob = review.jobs?.review;

    if (reviewJob?.id && this.isTerminal(reviewJob.status)) {
      choices.push({
        label: 'Reprocessar review',
        description: `Status atual: ${reviewJob.status ?? 'n/d'} · Tentativas: ${reviewJob.attempts ?? 0}`,
        jobKey: 'review',
        job: reviewJob
      });
    }

    const assistants = this.getAssistantJobs(review);

    // Review pronto e nenhuma análise: o passo que falta é o assistente, e ele não existe
    // para ser reprocessado. Sem esta opção o modal oferecia só refazer o review — que já
    // estava done — e não havia como avançar de dentro da lista.
    if (reviewJob?.id && reviewJob.status === 'done' && assistants.length === 0) {
      choices.push({
        label: 'Gerar análise',
        description: 'O review produziu os prompts, mas a análise nunca foi solicitada.',
        jobKey: 'assistant',
        job: reviewJob,
        kind: 'generate'
      });
    }

    for (const assistant of assistants) {
      if (!assistant?.id || !this.isTerminal(assistant.status)) {
        continue;
      }
      const promptLabel = assistant.promptType ? ` (${assistant.promptType})` : '';
      choices.push({
        label: `Reprocessar assistant${promptLabel}`,
        description: `Status atual: ${assistant.status ?? 'n/d'} · Tentativas: ${assistant.attempts ?? 0}`,
        jobKey: 'assistant',
        job: assistant
      });
    }

    return choices;
  }

  private getAssistantJobs(review: Review): SmartReviewJobFull[] {
    return (review.jobs?.assistants ?? []).filter(Boolean) as SmartReviewJobFull[];
  }

  private isTerminal(status?: string): boolean {
    return TERMINAL_STATUSES.includes((status || '').toLowerCase());
  }
}
